/*
 * Copyright (C) 2025 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and
 * should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly
 * prohibited.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import appTheme from '../../common/theme/appTheme.json';
import { Box, Button } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { GridActionsCellItem, GridColDef } from '@mui/x-data-grid';
import CustomDataGrid from '../../common/data_grid/CustomDataGrid';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import ModalDialog from '../../common/dialogs/ModalDialog';
import WarningIcon from '@mui/icons-material/Warning';
import { Link, useRouteMatch } from 'react-router-dom';
import ErrorMessage from '../../common/layout/ErrorMessage';
import { StyledProvisioningPageHeading } from '../StyledProvisioningPageHeading';
import ModalLoadingSpinner from '../../common/layout/ModalLoadingSpinner';
import { selectRoles, selectRolesRequestError, selectIsRequestingRoles } from '../../../store';
import CreateIcon from '@mui/icons-material/Create';
import { RenderTopAlignedFixedHeightCell } from '../../common/data_grid/TopAlignedCell';
import { deleteRole, requestRoles } from '../../../store/roles/actions';
import { Role } from '../../../shared/entities/role';
import { GLOBAL_SCOPE } from './Roles';

interface RolesTableProps {
  // Required for displaying tables in unit tests
  disableVirtualization?: boolean;
}

const RolesTable = ({ disableVirtualization = false }: RolesTableProps) => {
  const dispatch = useDispatch();
  const { path } = useRouteMatch();
  const loadingRoles = useSelector(selectIsRequestingRoles, shallowEqual);
  const rolesArray = useSelector(selectRoles, shallowEqual);
  const rolesRequestError = useSelector(selectRolesRequestError, shallowEqual);
  const [deleteModalTarget, setDeleteModalTarget] = useState<Role | undefined>(undefined);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  useEffect(() => {
    dispatch(requestRoles());
  }, [dispatch]);

  const rolesInTable = useMemo(() => {
    if (!rolesArray) {
      return [];
    }

    // If role has no orgName, default it to 'Global Scope'
    return rolesArray.map(
      role =>
        ({
          ...role,
          orgName: role.orgName || GLOBAL_SCOPE,
        } as Role),
    );
  }, [rolesArray]);

  const COLLAPSED_ROW_HEIGHT_PX = 49;
  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Role name',
      flex: 1,
      renderCell: RenderTopAlignedFixedHeightCell(COLLAPSED_ROW_HEIGHT_PX),
    },
    {
      field: 'description',
      headerName: 'Role description',
      flex: 2,
      renderCell: RenderTopAlignedFixedHeightCell(COLLAPSED_ROW_HEIGHT_PX),
    },
    {
      type: 'actions',
      field: 'actions',
      cellClassName: 'ActionsCell-TopAligned',
      headerName: '',
      align: 'right',
      flex: 1,
      getActions: params => [
        <GridActionsCellItem
          key={'create-role'}
          icon={<CreateIcon />}
          component={Link}
          to={`${path}/edit/${params.row.id}`}
        />,
        <GridActionsCellItem
          key={'delete-role'}
          icon={<DeleteIcon />}
          onClick={() => {
            setDeleteModalTarget(params.row);
            setShowDeleteModal(true);
          }}
        />,
      ],
    },
  ];

  const AddRoleButton = useCallback(
    () => (
      <Button
        component={Link}
        to={`${path}/new`}
        startIcon={<AddIcon />}
        variant="contained"
        sx={{
          marginLeft: 'auto',
          color: '#000000',
          letterSpacing: '0.8px',
          backgroundColor: appTheme.mptLightBlue,
          '&:hover': {
            backgroundColor: appTheme.mptLightBlue,
            svg: {
              color: '#000000',
            },
          },
        }}
      >
        Provision new role
      </Button>
    ),
    [path],
  );

  return (
    <>
      {rolesRequestError ? <ErrorMessage>{rolesRequestError}</ErrorMessage> : null}

      <StyledProvisioningPageHeading>
        Roles {rolesInTable ? `(${rolesInTable.length})` : ''}
      </StyledProvisioningPageHeading>

      {loadingRoles ? <ModalLoadingSpinner /> : null}

      <Box display="flex" justifyContent="space-between" flexDirection={'column'} height={'100%'}>
        <CustomDataGrid
          rows={rolesInTable || []}
          getRowHeight={() => 'auto'}
          getEstimatedRowHeight={() => COLLAPSED_ROW_HEIGHT_PX}
          columns={columns}
          initialState={{
            sorting: {
              sortModel: [{ field: 'roleName', sort: 'asc' }],
            },
          }}
          toolbarActionButtons={[<AddRoleButton key={'add-role-button'} />]}
          slotProps={{
            toolbar: {
              csvOptions: {
                fileName: 'Roles',
              },
            },
          }}
          disableVirtualization={disableVirtualization}
        />
      </Box>

      <ModalDialog
        open={showDeleteModal}
        onLeftBtnClicked={() => {
          setShowDeleteModal(false);
        }}
        onRightBtnClicked={() => {
          dispatch(deleteRole(deleteModalTarget!.id));
          setShowDeleteModal(false);
        }}
        onExited={() => {
          // Remove the delete target after the dialog's exit animation has finished. Otherwise, the `message` below
          // will briefly display `undefined` for the organization's display name
          setDeleteModalTarget(undefined);
        }}
        title={
          <Box display={'flex'} gap={'8px'} alignItems={'center'}>
            <WarningIcon htmlColor={'#FFB029'} /> Confirm Role Deletion
          </Box>
        }
        message={
          <>
            Are you sure you want to delete the role <code>{deleteModalTarget?.name}</code>?
            <br />
            <br />
            This action cannot be undone.
          </>
        }
      />
    </>
  );
};

export default RolesTable;
